import { Injectable, inject, signal } from '@angular/core';
import { AlertController } from '@ionic/angular';
import {
  BehaviorSubject, Observable, Subject, race,
  filter, map, pairwise, switchMap, take, throwError,
} from 'rxjs';
import { AUTH_CONFIG } from './auth.config';
import { LOGGER } from './shared/logger';
import { IdTokenRejectedError, InvalidGrantError, ScopeRejectedError, SessionLostError } from './errors';
import { extractProfile, extractRoles, IdTokenProfile, readSubject } from './id-token';
import { IdTokenVerifier } from './id-token-verifier';
import { LOGIN_LAUNCHER } from './login-launcher';
import { computeS256Challenge, generateCodeVerifier, generateState } from './pkce';
import { matchesRedirectUri } from './redirect-uri';
import { AppStateService } from './shared/app-state.service';
import { StoredTokens, TOKEN_STORAGE } from './token-storage';
import { environment } from 'src/environments/environment';
import { LogoutStatisticsService, SignOutType } from '../services/logout-statistics.service';

interface OpenIdConfiguration {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  jwks_uri: string;
  end_session_endpoint?: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  /** Only sent when the granted scope differs from the requested one. */
  scope?: string;
}

/**
 * All requests to the IdP (discovery, token, end-session) deliberately use `fetch` rather
 * than `HttpClient`. That is load-bearing, not a matter of taste: `HttpClient` calls pass
 * through `authInterceptor`, which holds authenticated requests until this very service
 * reports the token check as finished. A refresh issued that way would be waiting for
 * itself, and every authenticated request in the app would queue up behind it forever.
 *
 * The service owns the "auth settled" signal that interceptor waits on: unset at startup
 * and whenever the app goes to the background, set again after a startup/resume check or
 * a login — in the failure case too, so nothing can wait indefinitely.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly storage = inject(TOKEN_STORAGE);
  private readonly appState = inject(AppStateService);
  private readonly launcher = inject(LOGIN_LAUNCHER);
  private readonly alertCtrl = inject(AlertController);
  private readonly verifier = inject(IdTokenVerifier);
  private readonly log = inject(LOGGER).scope('authentication');
  /**
   * Goes through `HttpClient` rather than `fetch`, unlike everything this service sends to
   * the IdP: it targets our own API, not the token endpoint, and carries no `AUTHENTICATE`
   * context — so it passes straight through `authInterceptor` and cannot wait on the token
   * check this service is in the middle of performing.
   */
  private readonly logoutStatistics = inject(LogoutStatisticsService);

  private discoveryCache: OpenIdConfiguration | null = null;
  private refreshInFlight: Promise<StoredTokens> | null = null;
  /**
   * A rejected scope does not fix itself: every subsequent refresh asks for the same too
   * wide scope and is refused again. Reporting each attempt would bury the signal under
   * its own repetitions, so one report per app run is what gets sent.
   */
  private scopeRejectionReported = false;
  private tokens: StoredTokens | null = null;

  readonly isAuthenticated = signal(false);

  /**
   * Roles claimed by the ID token, for gating UI on `access_roles`.
   *
   * `null` means "not determined" and must never be read as "has no roles"; an empty
   * array means the user is genuinely anonymous. The initial value is `null` on purpose,
   * so every path that fails to establish an identity leaves it unknown and consumers
   * such as the homescreen reconciliation skip instead of acting on a wrong empty list.
   */
  readonly userRoles = signal<readonly string[] | null>(null);
  
  /**
   * Identity claims of the ID token (name, employee number, matriculation number, …), for
   * display and prefill purposes. `null` means "no profile available" — logged out as well
   * as an unreadable token; every field inside is itself optional, see `IdTokenProfile`.
   */
  readonly userProfile = signal<IdTokenProfile | null>(null);

  private readonly authSettled$ = new BehaviorSubject<boolean>(false);
  private readonly sessionLost$ = new Subject<void>();

  constructor() {
    this.launcher.callbackUrl$.subscribe(url => void this.handleCallbackUrl(url));

    // Going to the background invalidates the check: on resume the token may have expired
    // while the app was suspended, so it has to be settled again before it is used.
    this.appState.isActive$
      .pipe(filter(active => !active))
      .subscribe(() => this.authSettled$.next(false));

    this.appState.isActive$
      .pipe(pairwise(), filter(([prev, curr]) => !prev && curr))
      .subscribe(() => void this.onResume());
  }

  /**
   * For the auth interceptor: holds a request that carries a token until the startup or
   * resume check has finished, and aborts it with a `SessionLostError` if the session is
   * lost while it waits.
   *
   * Requests without `AUTHENTICATE` never come through here — waiting for a token check
   * buys them nothing, and a lost session is no reason for them to fail.
   */
  waitUntilAuthSettled(): Observable<void> {
    return race(
      this.authSettled$.pipe(filter(Boolean), take(1), map(() => void 0)),
      this.sessionLost$.pipe(
        take(1),
        switchMap(() => throwError(() => new SessionLostError())),
      ),
    );
  }

  private markAuthSettled(): void {
    this.log.debug('auth settled, authenticated requests may proceed');
    this.authSettled$.next(true);
  }

  /**
   * Single place where the publicly observable identity is derived from `this.tokens`.
   * Call it after every assignment to that field, so authentication state and roles can
   * never drift apart.
   */
  private syncIdentity(): void {
    const tokens = this.tokens;
    this.isAuthenticated.set(tokens != null);
    // Without tokens the user is definitely anonymous, which is an empty role list
    // rather than the "unknown" null.
    const roles = tokens ? extractRoles(tokens.idToken) : [];
    this.userRoles.set(roles);
    const profile = tokens ? extractProfile(tokens.idToken) : null;
    this.userProfile.set(profile);

    this.log.info('identity', {
      authenticated: tokens != null,
      // Roles drive which tiles are shown, so seeing them is what makes that traceable.
      roles: roles ?? 'unknown',
      // The subject identifies the session without spelling the person's name into a log.
      subject: profile?.username ?? null,
      expiresInSeconds: tokens ? Math.round((tokens.expiresAt - Date.now()) / 1000) : null,
    });
  }

  /**
   * Causes the authenticated requests currently waiting for the token check to fail with a
   * SessionLostError. Unauthenticated requests are unaffected — they never waited on it.
   */
  private abortWaitingRequests(): void {
    this.sessionLost$.next();
  }

  /**
   * The only entry point for APP_INITIALIZER.
   *
   * Must mark auth as settled in every case, including failure — otherwise authenticated
   * requests would queue up indefinitely.
   */
  async initialize(): Promise<void> {
    this.log.debug('startup check begins');
    try {
      await this.handleStartupRedirect();
      await this.initFromStorage();
    } catch (e: unknown) {
      /**
       * invalid_grant → Handled in handleInvalidGrant (user logged out).
       * Network error → tokens remain; 401 path takes effect later.
       */
      this.log.warn('startup check did not complete', { error: String(e) });
    } finally {
      this.markAuthSettled();
    }
  }

  /**
   * Processes the startup callback before anything else if the app launch itself came 
   * from the OAuth callback.
   */
  private async handleStartupRedirect(): Promise<void> {
    const url = await this.launcher.consumeStartupCallback();
    if (url) await this.handleCallbackUrl(url);
  }

  private async initFromStorage(): Promise<void> {
    try {
      if (!this.tokens) this.tokens = await this.storage.load();
      if (this.tokens && this.isExpired(this.tokens)) {
        await this.refresh();
      }
    } finally {
      /**
       * Must run even when the refresh threw, which it regularly does when the app starts
       * without a connection. The identity is then taken from the stored token — the last
       * known roles — rather than the app presenting itself as anonymous and consumers
       * acting on that.
       */
      this.syncIdentity();
    }
  }

  async login(): Promise<void> {
    this.log.info('login started');
    const config = await this.discovery();
    const verifier = generateCodeVerifier();
    const challenge = await computeS256Challenge(verifier);
    const state = generateState();
    const nonce = generateState();

    // Persist BEFORE `launcher.open()`: In redirect mode, the app is reloaded during
    // the callback, in-memory state will not survive that.
    await this.storage.savePendingLogin({ verifier, state, nonce });

    const url = new URL(config.authorization_endpoint);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', AUTH_CONFIG.clientId);
    url.searchParams.set('redirect_uri', this.launcher.redirectUri);
    url.searchParams.set('scope', AUTH_CONFIG.scope);
    url.searchParams.set('state', state);
    url.searchParams.set('nonce', nonce);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');

    await this.launcher.open(url.toString());
  }

  private async handleCallbackUrl(urlString: string): Promise<void> {
    if (!matchesRedirectUri(urlString, this.launcher.redirectUri)) return;
    await this.launcher.close();

    const pending = await this.storage.loadPendingLogin();
    await this.storage.clearPendingLogin();
    if (!pending) return;

    const params = new URL(urlString).searchParams;
    if (params.get('state') !== pending.state) {
      this.log.error('callback rejected: state does not match the authorization request');
      return;
    }
    const error = params.get('error');
    if (error) {
      this.log.error('provider reported a failure', {
        error,
        description: params.get('error_description'),
      });
      return;
    }
    const code = params.get('code');
    if (!code) {
      this.log.error('callback carried neither a code nor an error');
      return;
    }

    try {
      await this.exchangeCode(code, pending.verifier, pending.nonce);
      this.log.info('login completed');
    } catch (e: unknown) {
      // Nothing above catches this: the callback arrives through a subscription, so an
      // unhandled rejection here would leave the user logged out without explanation.
      this.log.error('code exchange failed', { error: String(e) });
    }
  }

  private async exchangeCode(code: string, verifier: string, nonce: string): Promise<void> {
    this.log.debug(`${code}, ${verifier}, ${nonce}`);
    const openIdConfiguration = await this.discovery();
    const codeExchangeResponse = await fetch(openIdConfiguration.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: this.launcher.redirectUri,
        code_verifier: verifier,
        client_id: AUTH_CONFIG.clientId,
      }),
    });
    if (!codeExchangeResponse.ok) {
      throw new Error(`Code-Exchange failed: HTTP ${codeExchangeResponse.status}`);
    }
    const body = (await codeExchangeResponse.json()) as TokenResponse;

    /**
     * Identity is established here, so an ID token that cannot be verified aborts the
     * login instead of being accepted. `openid` is part of the scope, so a response
     * without an ID token is itself a reason to stop.
     */
    if (!body.id_token) throw new IdTokenRejectedError('no id_token in the code exchange');
    await this.verifier.verify(body.id_token, {
      jwksUri: openIdConfiguration.jwks_uri,
      issuer: openIdConfiguration.issuer,
      nonce,
    });

    const tokens = this.mapTokenResponse(body, null);
    await this.storage.save(tokens);
    this.tokens = tokens;
    this.syncIdentity();
    this.markAuthSettled();
  }

  refresh(): Promise<StoredTokens> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshTokens()
        .catch(async (e: unknown) => {
          if (e instanceof InvalidGrantError) await this.handleInvalidGrant();
          throw e;
        })
        .finally(() => {
          this.refreshInFlight = null;
        });
    }
    return this.refreshInFlight;
  }

  private async refreshTokens(): Promise<StoredTokens> {
    /**
     * Adopts what storage held instead of only reading it, the same way `initFromStorage`
     * and `getValidAccessToken` do. That is what lets `handleInvalidGrant` treat a null
     * `this.tokens` as "the logout already ran": without the assignment the field could be
     * null while storage still held a session, and the guard there would skip a logout
     * that genuinely needed to happen.
     */
    if (!this.tokens) this.tokens = await this.storage.load();
    const storedTokens = this.tokens;
    if (!storedTokens?.refreshToken) throw new InvalidGrantError();

    const openIdConfiguration = await this.discovery();
    const refreshResponse = await fetch(openIdConfiguration.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: storedTokens.refreshToken,
        client_id: AUTH_CONFIG.clientId,
        /**
         * Sent explicitly so the response carries a fresh ID token: providers are not
         * obliged to include one in a refresh, and without it the roles would stay frozen
         * at whatever the last login reported.
         *
         * Uses the granted scope where the IdP told us what it granted, because repeating
         * a wider scope than granted is rejected outright.
         *
         * @TODO Handle a GROWING scope. A refresh can never obtain more than was granted
         * at login, so once a release adds a scope to `AUTH_CONFIG.scope`, already
         * logged-in users keep refreshing on the old, narrower grant — and whatever
         * feature needs the new scope fails for them, silently and indefinitely. Only a
         * fresh login can widen it.
         *
         * Sketch: compare `AUTH_CONFIG.scope` against `storedTokens.scope` after every
         * refresh; if the config asks for scopes the grant does not contain, offer a
         * re-login instead of forcing one — being thrown to the login screen without
         * explanation is worse than a delayed feature. Ask at most once per release, and
         * remember a refusal so it does not turn into a nagging prompt.
         *
         * The wording has to work for someone who has never heard of OAuth. No "scope",
         * no "token", no "permissions" in the technical sense. Draft:
         *
         *   Header:  "Neue Funktionen verfügbar"
         *   Message: "Diese Version der App kann mehr als die vorherige. Damit die neuen
         *             Funktionen dein Uni-Konto nutzen dürfen, musst du dich einmal neu
         *             anmelden. Du kannst die App auch ohne das weiter wie gewohnt
         *             verwenden — die neuen Funktionen bleiben dann aber leer oder melden
         *             einen Fehler, bis du dich das nächste Mal anmeldest."
         *   Buttons: "Später"  |  "Jetzt neu anmelden"
         *
         * Note the second button ends the current session, so anything unsaved has to be
         * persisted before `login()` is called.
         */
        scope: storedTokens.scope ?? AUTH_CONFIG.scope,
      }),
    });

    if (!refreshResponse.ok) {
      // ONLY `invalid_grant` is a reason for logout. 5xx/network errors: Retain tokens, propagate the error
      let errorCode = '';
      try {
        errorCode = ((await refreshResponse.json()) as { error?: string }).error ?? '';
      } catch {
        /* body not parsable: no invalid_grant */
      }
      if (refreshResponse.status === 400 && errorCode === 'invalid_grant') {
        throw new InvalidGrantError();
      }
      if (refreshResponse.status === 400 && errorCode === 'invalid_scope') {
        /**
         * The session stays: the refresh token is fine, only the scope we asked for is
         * wider than what was granted. Reported nonetheless — this is the failure mode the
         * `@TODO` above describes, where authenticated endpoints quietly stop working for
         * everyone who was logged in before the release that widened the scope. Without a
         * counter it looks like nothing at all from the outside.
         */
        const requestedScope = storedTokens.scope ?? AUTH_CONFIG.scope;
        this.log.error('refresh rejected: requested scope exceeds the granted one', {
          requested: requestedScope,
          granted: storedTokens.scope ?? null,
        });
        if (!this.scopeRejectionReported) {
          this.scopeRejectionReported = true;
          this.logoutStatistics.report(SignOutType.SCOPE_REJECTED);
        }
        throw new ScopeRejectedError(requestedScope, storedTokens.scope ?? null);
      }
      throw new Error(`token refresh failed: HTTP ${refreshResponse.status}`);
    }

    const body = (await refreshResponse.json()) as TokenResponse;
    const tokens = this.mapTokenResponse(
      {
        ...body,
        id_token: (await this.acceptRefreshedIdToken(body.id_token ?? null, storedTokens)) ?? undefined,
      },
      storedTokens,
    );

    // persist the data BEFORE the promise resolves — otherwise, a consumer could continue working with the old (rotated) refresh token.
    await this.storage.save(tokens);
    this.tokens = tokens;
    // A refresh can carry changed claims, so the roles are re-derived here too.
    this.syncIdentity();
    return tokens;
  }

  /**
   * Unlike the code exchange, a rejected ID token here does NOT end the session: access to
   * the API hangs off the access token, and the backend validates that itself. Dropping the
   * user out over an unverifiable ID token would turn a JWKS hiccup into a mass logout.
   *
   * The token is simply not adopted — `mapTokenResponse` then keeps the previous, verified
   * one, so no unverified claims ever reach `syncIdentity` and the roles.
   */
  private async acceptRefreshedIdToken(
    idToken: string | null,
    previous: StoredTokens,
  ): Promise<string | null> {
    if (!idToken) return null;
    try {
      const openIdConfiguration = await this.discovery();
      await this.verifier.verify(idToken, {
        jwksUri: openIdConfiguration.jwks_uri,
        issuer: openIdConfiguration.issuer,
        // No nonce on this path: it belongs to the authorization request, not to a refresh.
        expectedSubject: readSubject(previous.idToken ?? null) ?? undefined,
      });
      return idToken;
    } catch (e: unknown) {
      this.log.warn('refreshed ID token not accepted, keeping the previous one', {
        error: String(e),
      });
      return null;
    }
  }

  /** Für den Auth-Interceptor: liefert gültiges Access Token oder null. */
  async getValidAccessToken(): Promise<string | null> {
    if (!this.tokens) this.tokens = await this.storage.load();
    if (!this.tokens) return null;
    if (this.isExpired(this.tokens)) {
      try {
        await this.refresh();
      } catch {
        return null;
      }
    }
    return this.tokens?.accessToken ?? null;
  }

  private async onResume(): Promise<void> {
    try {
      if (this.tokens && this.isExpired(this.tokens)) {
        await this.refresh();
      }
    } catch {
      // invalid_grant: already handled centrally
      // If a network error occurs, auth counts as settled anyway
    } finally {
      this.markAuthSettled();
    }
  }

  async logout(afterLogout: () => {}): Promise<void> {
    try {
      const url = await this.buildEndSessionUrl();
      if (url) await fetch(url).catch(() => undefined);
    } finally {
      await this.logoffLocal();
      afterLogout();
    }
  }

  private async buildEndSessionUrl(): Promise<string | null> {
    const config = await this.discovery();
    if (!config.end_session_endpoint) return null;

    const url = new URL(config.end_session_endpoint);
    if (this.tokens?.idToken) url.searchParams.set('id_token_hint', this.tokens.idToken);
    url.searchParams.set('client_id', AUTH_CONFIG.clientId);
    return url.toString();
  }

  private async logoffLocal(): Promise<void> {
    await this.storage.clear();
    await this.storage.clearPendingLogin();
    this.tokens = null;
    this.syncIdentity();
  }

  // TODO: extract alert code into a callback or something
  private async handleInvalidGrant(): Promise<void> {
    /**
     * Only the first rejection is a session loss; the ones behind it are its echo.
     *
     * Several authenticated requests can be in flight at once, and `abortWaitingRequests`
     * reaches only those still waiting for the token check — the rest collect their own
     * 401 afterwards and each asks the interceptor for a refresh. `refreshInFlight`
     * merges the simultaneous ones, but a 401 arriving after the first refresh settled
     * starts a new one, which `refreshTokens` rejects outright because the tokens are
     * gone. Without this guard that would mean a second alert stacked on the first, and a
     * logout counted as many times as there happened to be requests running.
     */
    if (!this.tokens) {
      this.log.debug('invalid_grant after the session was already given up, ignoring');
      return;
    }

    this.log.warn('session lost: provider rejected the refresh token');
    // The user did not ask for this, so it is the one logout worth counting centrally.
    this.logoutStatistics.report(SignOutType.REFRESH_TOKEN_INVALID);
    await this.logoffLocal();
    this.abortWaitingRequests();

    const alert = await this.alertCtrl.create({
      header: 'Sitzung abgelaufen',
      message: 'Deine Anmeldung ist abgelaufen. Möchtest du dich neu anmelden?',
      backdropDismiss: false,
      buttons: [
        { text: 'Abbrechen', role: 'cancel' },
        { text: 'Anmelden', handler: () => void this.login() },
      ],
    });
    await alert.present();
  }

  private async discovery(): Promise<OpenIdConfiguration> {
    if (this.discoveryCache) return this.discoveryCache;
    // Only on a cache miss, so this marks the one network round trip the start waits for.
    this.log.debug('fetching discovery document');
    const url = `${AUTH_CONFIG.issuer.replace(/\/$/, '')}/.well-known/openid-configuration`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`OIDC-Discovery failed: HTTP ${res.status}`);
    const config = (await res.json()) as OpenIdConfiguration;

    /**
     * `jwks_uri` decides which keys are trusted for ID token signatures, so it must not
     * point somewhere else than the configured provider. Note that `config.issuer` is
     * NOT compared with `AUTH_CONFIG.issuer`: this IdP advertises
     * `…:443/openam/oauth2` while the app is configured with the realm URL, and the
     * advertised value is the one the tokens carry.
     */
    if (!config.jwks_uri) throw new Error('OIDC-Discovery: jwks_uri missing');
    if (new URL(config.jwks_uri).host !== new URL(AUTH_CONFIG.issuer).host) {
      throw new Error('OIDC-Discovery: jwks_uri points at a different host than the issuer');
    }

    this.discoveryCache = config;
    return this.discoveryCache;
  }

  private isExpired(tokens: StoredTokens): boolean {
    return Date.now() >= tokens.expiresAt - AUTH_CONFIG.expiryBufferSeconds * 1000;
  }

  /**
   * Test helper: rewrites `expiresAt` into the past, in memory and in storage, so every
   * expiry check treats the access token as expired. The next authenticated request (or
   * the next resume) then runs a real refresh against the IdP — this alone exercises the
   * silent-refresh path with the still-valid refresh token.
   */
  async debugExpireAccessToken(): Promise<void> {
    await this.debugMutateTokens('access token marked as expired', tokens => ({
      ...tokens,
      expiresAt: 0,
    }));
  }

  /**
   * Test helper: replaces the stored refresh token with garbage. On its own nothing
   * happens until the access token expires — combine it with `debugExpireAccessToken()`
   * to force the next refresh immediately, which the IdP answers with `invalid_grant`
   * and the session-lost flow (logout + re-login alert) takes over.
   *
   * The real refresh token is gone afterwards, so this test always ends in a fresh login.
   */
  async debugSetBogusRefreshToken(): Promise<void> {
    await this.debugMutateTokens('refresh token replaced with a bogus value', tokens => ({
      ...tokens,
      refreshToken: 'debug-bogus-refresh-token',
    }));
  }

  /**
   * Test helper: removes the refresh token but keeps the rest of the session.
   *
   * Hits the OTHER route into `InvalidGrantError` — the local one in `refreshTokens`,
   * which throws before any request is made. `debugSetBogusRefreshToken` exercises the
   * IdP's verdict; this one exercises what happens when there is nothing to ask with.
   * Same outcome for the user, different code path, so both are worth seeing once.
   */
  async debugDropRefreshToken(): Promise<void> {
    await this.debugMutateTokens('refresh token removed', tokens => ({
      ...tokens,
      refreshToken: undefined,
    }));
  }

  /**
   * Test helper: widens the stored scope by one that was never granted, which is exactly
   * what a release adding a scope to `AUTH_CONFIG.scope` does to everyone already logged
   * in. The next refresh asks for more than the grant covers and the IdP refuses it.
   *
   * Expected: `invalid_scope`, a `ScopeRejectedError`, one `scope_rejected` report — and
   * NO logout, the session stays. Whether this provider answers `invalid_scope` rather
   * than something else is precisely what is being checked here, so read the log rather
   * than assuming it.
   */
  async debugRequestUnknownScope(): Promise<void> {
    await this.debugMutateTokens('stored scope widened by one that was never granted', tokens => ({
      ...tokens,
      scope: `${tokens.scope ?? AUTH_CONFIG.scope} urn:debug:never-granted`,
    }));
  }

  /**
   * Test helper: refreshes right now, rather than waiting for an expiry or a request to
   * trigger one. Reports the outcome to the log and swallows it otherwise.
   *
   * Run it twice in a row after the session is gone to watch the `handleInvalidGrant`
   * guard do its job: the first call logs out and shows the alert, the second has to stay
   * silent. That is the double-counting case, and it is the only way to see it on demand
   * instead of hoping several requests happen to fail at once.
   */
  async debugForceRefresh(): Promise<void> {
    if (environment.production) {
      this.log.warn('debug token manipulation is disabled in production builds');
      return;
    }
    try {
      const tokens = await this.refresh();
      this.log.warn('debug: refresh succeeded', {
        expiresInSeconds: Math.round((tokens.expiresAt - Date.now()) / 1000),
      });
    } catch (error: unknown) {
      this.log.warn('debug: refresh failed', { error: String(error) });
    }
  }

  /**
   * Persists BEFORE assigning, mirroring the order the real token paths use — and only
   * outside production builds, so no release can ever reach a token-corrupting code path.
   */
  private async debugMutateTokens(
    what: string,
    mutate: (tokens: StoredTokens) => StoredTokens,
  ): Promise<void> {
    if (environment.production) {
      this.log.warn('debug token manipulation is disabled in production builds');
      return;
    }
    if (!this.tokens) this.tokens = await this.storage.load();
    if (!this.tokens) {
      this.log.warn('debug: no tokens to manipulate — log in first');
      return;
    }
    const tokens = mutate(this.tokens);
    await this.storage.save(tokens);
    this.tokens = tokens;
    this.syncIdentity();
    this.log.warn(`debug: ${what}`);
  }

  private mapTokenResponse(body: TokenResponse, previous: StoredTokens | null): StoredTokens {
    return {
      accessToken: body.access_token,
      // When a rotation occurs, the IdP issues a new refresh_token;
      // otherwise, the previous one remains valid.
      refreshToken: body.refresh_token ?? previous?.refreshToken,
      idToken: body.id_token ?? previous?.idToken,
      expiresAt: Date.now() + (body.expires_in ?? 300) * 1000,
      // Absent means "as requested", so the previously known grant still applies.
      scope: body.scope ?? previous?.scope,
    };
  }
}
